import { Component, For, Show } from "solid-js"
import { getEffectiveTime, isDefaultStartPin } from "../lib/viewport-utils"
import { useScenarioStore } from "../stores/scenario"
import { formatSimTime } from "../types"

const ZOOM_OPTIONS = [
	{ value: 20, label: "Max zoom" },
	{ value: 18, label: "Medel" },
	{ value: 16, label: "Översikt" },
]

interface ViewportPanelProps {
	selectedPinId?: string | null
	onSelectPin?: (pinId: string | null) => void
}

const ViewportPanel: Component<ViewportPanelProps> = (props) => {
	// Use the new ScenarioStore hook
	const [scenario, scenarioActions] = useScenarioStore()
	
	const handleAdd = () => {
		scenarioActions.addViewportPin(false)
		scenarioActions.scheduleSave()
	}

	const handleTimeChange = (
		id: string,
		field: "hour" | "minute" | "second",
		value: string,
	) => {
		const pin = scenario.viewportPins.find((p) => p.id === id)
		if (!pin) return
		
		// First pin time is "default-start" and cannot be changed manually
		if (isDefaultStartPin(pin)) return

		// Allow empty string (user is typing)
		if (value === "") {
			scenarioActions.updateViewportPin(id, { time: { ...(pin.time as any), [field]: 0 } })
			return
		}

		const num = parseInt(value, 10)
		if (isNaN(num)) return
		scenarioActions.updateViewportPin(id, { time: { ...(pin.time as any), [field]: num } })
		scenarioActions.scheduleSave()
	}

	const handleZoomChange = (id: string, value: string) => {
		const zoom = parseInt(value, 10)
		if (zoom !== 16 && zoom !== 18 && zoom !== 20) return
		scenarioActions.updateViewportPin(id, { zoom: zoom as 16 | 18 | 20 })
		scenarioActions.scheduleSave()
	}

	const handleToggleAutoPan = () => {
		scenarioActions.setAutoPan(!scenario.autoPan)
		scenarioActions.scheduleSave()
	}

	const isPinSet = (pin: any) => {
		return pin.position[0] !== 0 || pin.position[1] !== 0
	}

	return (
		<>
			<div class="border-b border-gray-200 bg-white p-3">
				<h2 class="mb-2 text-sm font-semibold text-gray-700">
					Kamerapositioner
				</h2>
				<p class="mb-2 text-[10px] text-gray-400">
					Lägg till vyplats och välj den från listan. Klicka på kartan för att placera den. Klicka sedan på markören för att aktivera redigeringsläge (fylld cirkel). Ändra position med Shift+dra eller zooma med rullgardinsmenyn.
				</p>

				{/* Story Mode Toggle */}
				<Show when={scenario.viewportPins.length > 0}>
					<label class="mb-2 flex cursor-pointer items-center gap-1.5 text-xs text-gray-600 select-none">
						<input
							type="checkbox"
							checked={scenario.autoPan}
							onChange={handleToggleAutoPan}
							class="h-3 w-3 accent-blue-500"
						/>
						<span>
							Berättarläge (kartan följer vyplatserna automatiskt)
						</span>
					</label>
				</Show>

				<button
					class="w-full rounded bg-blue-500 px-2 py-1.5 text-xs font-medium text-white transition-colors hover:bg-blue-600"
					onClick={handleAdd}
				>
					+ Lägg till vyplats
				</button>
			</div>

			<div class="flex-1 overflow-y-auto">
				<For each={scenario.viewportPins}>
					{(pin, index) => {
						const isDefaultPin = () => isDefaultStartPin(pin)
						const isSelected = () => props.selectedPinId === pin.id
						
						return (
						<div 
							class={`space-y-1.5 border-b border-gray-200 px-3 py-2 cursor-pointer hover:bg-gray-50 ${
								isSelected() ? 'bg-blue-50 border-l-2 border-l-blue-500' : ''
							}`}
							onClick={() => props.onSelectPin?.(isSelected() ? null : pin.id)}
						>
							<div class="flex items-center justify-between">
								<div class="flex items-center gap-1">
									<span class="text-xs">📍</span>
									<span class="font-mono text-xs text-gray-500">
									{formatSimTime(getEffectiveTime(pin, scenario.meta.startTime))}
									</span>
									<Show when={isDefaultPin()}>
										<span class="text-[10px] text-gray-400">(starttid)</span>
									</Show>
								</div>
								<Show when={!isDefaultPin()}>
									<button
										class="text-xs text-red-400 hover:text-red-600"
										onClick={(e) => {
											e.stopPropagation()
											scenarioActions.removeViewportPin(pin.id)
											scenarioActions.scheduleSave()
										}}
									>
										×
									</button>
								</Show>
							</div>

							{/* Time inputs - only for non-default pins */}
							<Show when={!isDefaultPin()}>
							<div class="flex gap-1">
								<input
									type="number"
									min="0"
									max="23"
								value={(pin.time as any).hour}
									onInput={(e) =>
										handleTimeChange(
											pin.id,
											"hour",
											e.currentTarget.value,
										)
									}
									class="w-12 rounded border border-gray-300 px-1 py-0.5 text-center text-xs"
									placeholder="HH"
								/>
								<span class="self-center text-xs text-gray-400">:</span>
								<input
									type="number"
									min="0"
									max="59"
								value={(pin.time as any).minute}
									onInput={(e) =>
										handleTimeChange(
											pin.id,
											"minute",
											e.currentTarget.value,
										)
									}
									class="w-12 rounded border border-gray-300 px-1 py-0.5 text-center text-xs"
									placeholder="MM"
								/>
								<span class="self-center text-xs text-gray-400">:</span>
								<input
									type="number"
									min="0"
									max="59"
								value={(pin.time as any).second}
									onInput={(e) =>
										handleTimeChange(
											pin.id,
											"second",
											e.currentTarget.value,
										)
									}
									class="w-12 rounded border border-gray-300 px-1 py-0.5 text-center text-xs"
									placeholder="SS"
								/>
							</div>
							</Show>

							{/* Zoom dropdown */}
							<div>
								<label class="mb-0.5 block text-[10px] text-gray-500">
									Zoomnivå
								</label>
								<select
									value={pin.zoom}
									onChange={(e) =>
										handleZoomChange(pin.id, e.currentTarget.value)
									}
									class="w-full rounded border border-gray-300 px-2 py-1 text-xs"
								>
									<For each={ZOOM_OPTIONS}>
										{(opt) => (
											<option value={opt.value}>{opt.label}</option>
										)}
									</For>
								</select>
							</div>

							{/* Position status */}
							<div class="flex items-center gap-1 text-[10px]">
								{isPinSet(pin) ? (
									<span class="text-green-600">✓ Position satt</span>
								) : isSelected() ? (
								<span class="text-orange-500">⚠ Klicka på kartan för att placera</span>
							) : (
								<span class="text-gray-400">⚠ Markera först för att visa på kartan</span>
								)}
							</div>
						</div>
						)
					}}
				</For>
			</div>
		</>
	)
}

export default ViewportPanel
